import React, { useState, useEffect, useMemo } from "react";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../../../services/EventsService";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../../../services/AthleteService";
import { registerCoach } from "../../../services/CoachService";
import { PostPayment } from "../../../services/PaymentService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

function PaymentOption() {
  const apiInstance = useAxiosInstance();
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [showPayWidget, setShowPayWidget] = useState(false);
  const [spotRegPricing, setSpotRegPricing] = useState({
    combinePrice: 0,
    annualPrice: 0,
    combinePosDrillPrice: 0,
    monthlyPrice: 0,
  });
  const [adminPricing, setAdminPricing] = useState({
    monthlyPrice: 0,
    annualPrice: 0,
    combinePrice: 0,
  });
  const [isRegisteringFree, setIsRegisteringFree] = useState(false);
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);

  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const spotRegPayOptions = useMemo(() => {
    return JSON.parse(localStorage.getItem("subscriptionDetails") || "{}");
  }, []);

  // Read pre-validated coupon applied in AthleteMembershipPlans (spot flow only)
  const discountInfo = useMemo(() => {
    const isSpotFlow =
      Object.keys(
        JSON.parse(localStorage.getItem("subscriptionDetails") || "{}"),
      ).length > 0;
    if (!isSpotFlow)
      return {
        amount: 0,
        baseAmount: 0,
        percentage: 0,
        discountType: "",
        couponType: "",
      };
    try {
      const saved = localStorage.getItem("appliedCouponData");
      return saved
        ? JSON.parse(saved)
        : {
            amount: 0,
            baseAmount: 0,
            percentage: 0,
            discountType: "",
            couponType: "",
          };
    } catch {
      return {
        amount: 0,
        baseAmount: 0,
        percentage: 0,
        discountType: "",
        couponType: "",
      };
    }
  }, []);
  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const normalizeUser = (u) => ({
    ...u,
    id: u?.id ?? u?.userId ?? 0,
    registered: Boolean(u?.registered),
  });
  const accounts = useSelector((state) => state.account.register);
  const user = normalizeUser(accounts.length ? accounts[0] : storedData);
  const HighSchoolCoach = user?.role?.toLowerCase?.() === "highschool";
  const navigate = useNavigate();

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    if (user.role !== "spotcombine") {
      localStorage.removeItem("subscriptionDetails");
    }
  }, [user.role]);

  useEffect(() => {
    trackEvent("Registration4_ViewedPaymentPage", {
      role: user.role,
      portal_type: "highschool",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchPrices = async () => {
      let userPricing = "";
      let combineData = "";
      try {
        setIsLoading(true);
        if (HighSchoolCoach) {
          setShowPayWidget(false);
          return;
        }
        if (user.eventId) {
          combineData = await GetEventCombinePrice(apiInstance, user.eventId);
        }
        const adminPrices = await GetAdminPrice(apiInstance);

        const athletePricing = adminPrices?.data?.find(
          (option) => option.role.toLowerCase() === "athlete",
        );

        if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
          setSpotRegPricing({
            combinePrice: combineData?.data?.combinePrice ?? 0,
            annualPrice: athletePricing?.pricingOptions?.annualPrice ?? 0,
            combinePosDrillPrice: combineData?.data?.positionDrillPrice,
            monthlyPrice: athletePricing?.pricingOptions?.monthlyPrice ?? 0,
          });
          trackEvent("Registration3_ViewedPricing", {
            event_id: user.eventId,
            combine_price: combineData?.data?.combinePrice ?? 0,
            portal_type: "highschool",
          });
          setShowPayWidget(true);
        } else {
          userPricing = adminPrices.data.find(
            (option) => option.role.toLowerCase() === user.role.toLowerCase(),
          );
        }

        if (userPricing) {
          setAdminPricing(userPricing.pricingOptions);
          setShowPayWidget(true);
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [
    apiInstance,
    HighSchoolCoach,
    spotRegPayOptions,
    user.eventId,
    user.role,
    trackEvent,
  ]);

  const basePrice =
    spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0
      ? (spotRegPayOptions.combine ? spotRegPricing.combinePrice : 0) +
        (spotRegPayOptions.positionDrill
          ? spotRegPricing.combinePosDrillPrice
          : 0) +
        (spotRegPayOptions.subscription ? spotRegPricing.annualPrice : 0)
      : subscriptionType === "monthly"
        ? adminPricing.monthlyPrice
        : adminPricing.annualPrice;

  const selectedPrice = Math.max(
    basePrice - Number(discountInfo.amount || 0),
    0,
  );

  const handleToggle = (type) => {
    setSubscriptionType(type);
    setShowPayWidget(true);
    trackEvent("Registration4_ToggledSubscriptionType", {
      subscription_type: type,
      portal_type: "highschool",
    });
  };

  const finalSubscriptionType = useMemo(() => {
    if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
      const result = [];
      if (spotRegPayOptions.combine) result.push("combine");
      if (spotRegPayOptions.positionDrill) result.push("positionDrill");
      if (spotRegPayOptions.subscription) result.push("subscription");
      return result;
    } else {
      return subscriptionType;
    }
  }, [spotRegPayOptions, subscriptionType]);

  const staticUser = useMemo(
    () => ({
      email: user?.email,
      userId: user.userId,
      price: selectedPrice,
      eventId: user.eventId,
    }),
    [user?.email, user.userId, selectedPrice, user.eventId],
  );

  useEffect(() => {
    if (HighSchoolCoach) return;
    if (staticUser) {
      dispatch(
        setSubscriptionDetails({
          subscriptionType: finalSubscriptionType,
          subscriptionPrice: staticUser.price,
        }),
      );
    }
  }, [dispatch, finalSubscriptionType, HighSchoolCoach, staticUser]);

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponData");
    localStorage.removeItem("appliedCouponType");
  };

  const handleRegister = async () => {
    const currentUser = user;
    const role = currentUser?.role?.toLowerCase?.();
    let newUserId = currentUser.userId;
    setIsRegisteringFree(true);
    trackEvent("Registration4_InitiatedPayment", {
      event_id: currentUser.eventId,
      selected_price: selectedPrice,
      portal_type: "highschool",
    });

    try {
      if (HighSchoolCoach) {
        if (!currentUser.registered) {
          const reg = await registerCoach(apiInstance, currentUser);
          if (reg.status !== 200) {
            throw new Error("High school registration failed.");
          }
        }

        toast.success("Registration successful!", {
          position: "top-right",
        });
        localStorage.removeItem("appliedCouponCode");
        localStorage.removeItem("appliedCouponData");
        localStorage.removeItem("appliedCouponType");
        localStorage.removeItem("subscriptionDetails");
        localStorage.removeItem("registeredAccount");
        navigate("/register/registrationsuccess");
        return;
      }

      if (!currentUser.registered) {
        if (role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Athlete registration failed.");
        } else if (role === "university") {
          const reg = await registerCoach(apiInstance, currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Coach registration failed.");
        } else if (role === "spotcombine") {
          const reg = await registerAthleteWithEventId(
            apiInstance,
            currentUser,
            currentUser.eventId,
          );
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Spot combine registration failed.");
        }
      }
      let paymentPayloads = [];

      if (spotRegPayOptions?.combine) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: true,
          isPositionDrill: false,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.positionDrill) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: true,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.subscription) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: true,
          isMonthlySubscription: false,
          isCash: false,
          eventId: 0,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (!spotRegPayOptions || Object.keys(spotRegPayOptions).length === 0) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: subscriptionType === "annual",
          isMonthlySubscription: subscriptionType === "monthly",
          isCash: false,
          eventId: 0,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      for (const payload of paymentPayloads) {
        const paid = await PostPayment(apiInstance, payload);
        if (paid.status !== 200) {
          throw new Error("Payment failed.");
        }
      }

      const couponCode = localStorage.getItem("appliedCouponCode");
      if (couponCode) {
        await postAppliedCouponCode(couponCode);
      }

      toast.success("Registration and payment successful!", {
        position: "top-right",
      });
      trackEvent("Registration5_RegistrationCompleted", {
        event_id: user.eventId,
        payment_method: "free",
        portal_type: "highschool",
      });

      setTimeout(() => {
        navigate("/register/registrationsuccess");
      }, 1000);
    } catch (error) {
      trackEvent("Registration4_5_PaymentFailed", {
        event_id: user.eventId,
        reason: error.message ?? "unknown",
        portal_type: "highschool",
      });
      toast.error(error.message || "Registration or payment failed", {
        position: "top-right",
      });
    } finally {
      setIsRegisteringFree(false);
    }
  };

  useEffect(() => {
    if (HighSchoolCoach) {
      setShowPayWidget(false);
      setIsIntellipayReady(false);
      return;
    }
    if (selectedPrice === 0) {
      setShowPayWidget(false);
      setIsIntellipayReady(false);
    } else {
      setShowPayWidget(true);
    }
  }, [HighSchoolCoach, selectedPrice]);

  return (
    <>
      <div className="flex items-center justify-center mt-6">
        <div className="w-full max-w-4xl px-3">
          <div className="text-center text-white uppercase text-2xl xl:text-3xl lg:text-3xl font-black italic">
            Simple, Transparent Pricing
          </div>
          <p className="text-center text-white text-base font-normal py-2">
            Clear, straightforward pricing with no hidden fees.
          </p>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-4xl text-white"
              />
            </div>
          ) : (
            <>
              {HighSchoolCoach ? (
                <div className="mt-6 grid justify-items-center">
                  <div className="w-96 xl:w-96 lg:w-96 m-auto mt-6">
                    <div className="rounded-2xl border border-white-600">
                      <div className="flex flex-col px-6 py-6 text-white space-y-3">
                        <div className="flex justify-between font-bold text-lg">
                          <span>High School Registration</span>
                          <span>$0.00</span>
                        </div>
                        <div className="text-sm text-gray-200">
                          Subscription and payment are not required for this
                          role.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleRegister}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleRegister();
                      }
                    }}
                    className={`mt-4 text-white uppercase rounded px-12 py-2 cursor-pointer text-center ${
                      isRegisteringFree
                        ? "bg-gray-500 cursor-not-allowed"
                        : "bg-green-500 hover:bg-green-700"
                    }`}
                  >
                    {isRegisteringFree ? "Registering..." : "Register"}
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-6 grid justify-items-center">
                    {(!spotRegPayOptions ||
                      Object.keys(spotRegPayOptions).length === 0) && (
                      <div className="price-top flex gap-4">
                        <button
                          onClick={() => handleToggle("monthly")}
                          className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "monthly" ? "bg-red-500" : "bg-gray-600"}`}
                        >
                          Monthly
                        </button>

                        <button
                          onClick={() => handleToggle("annual")}
                          className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "annual" ? "bg-red-500" : "bg-gray-600"}`}
                        >
                          Annual
                        </button>
                      </div>
                    )}

                    <div className="w-96 xl:w-96 lg:w-96 m-auto mt-6">
                      <div className="rounded-2xl border border-white-600">
                        <div className="flex flex-col px-6 py-4 text-white space-y-3">
                          {spotRegPayOptions &&
                          Object.keys(spotRegPayOptions).length > 0 ? (
                            <>
                              {spotRegPayOptions.combine && (
                                <div className="flex justify-between">
                                  <span>Combine Pass</span>
                                  <span>
                                    $
                                    {(spotRegPricing.combinePrice ?? 0).toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                              )}
                              {spotRegPayOptions.positionDrill && (
                                <div className="flex justify-between">
                                  <span>Position Drills</span>
                                  <span>
                                    $
                                    {(
                                      spotRegPricing.combinePosDrillPrice ?? 0
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              )}
                              {spotRegPayOptions.subscription && (
                                <div className="flex justify-between">
                                  <span>Annual Subscription</span>
                                  <span>
                                    $
                                    {(spotRegPricing.annualPrice ?? 0).toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between">
                                <span>
                                  {subscriptionType === "monthly"
                                    ? "Monthly Subscription"
                                    : "Annual Subscription"}
                                </span>
                                <span>
                                  $
                                  {(subscriptionType === "monthly"
                                    ? (adminPricing.monthlyPrice ?? 0)
                                    : (adminPricing.annualPrice ?? 0)
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}

                          {discountInfo.amount > 0 && (
                            <>
                              {discountInfo.discountType === "Percent Off" && (
                                <div className="flex justify-between text-green-400 font-medium text-sm mt-2">
                                  <span>
                                    Discount ({discountInfo.percentage}%)
                                  </span>
                                  <span>
                                    - $
                                    {Number(discountInfo.amount || 0).toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between text-green-400 font-medium text-sm">
                                <span>Discount Amount</span>
                                <span>
                                  - $
                                  {Number(discountInfo.amount || 0).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                          <div className="border-t border-white-600 my-2"></div>
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total:</span>
                            <span>${(selectedPrice ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid justify-items-center">
                    <div className="grid justify-items-center">
                      {selectedPrice === 0 ? (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handleRegister}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleRegister();
                            }
                          }}
                          className="bg-green-500 mt-4 text-white uppercase rounded px-12 py-2 hover:bg-green-700 cursor-pointer text-center"
                        >
                          Register
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`mt-4 text-white uppercase rounded px-12 py-2 ipayfield
                        ${isIntellipayReady ? "bg-red-500 hover:bg-red-700 cursor-pointer" : "bg-gray-500 cursor-not-allowed opacity-70"}`}
                          data-ipayname="submit"
                          disabled={!isIntellipayReady}
                          aria-busy={!isIntellipayReady}
                          onClick={(e) => {
                            if (!isIntellipayReady) {
                              e.preventDefault();
                              toast.info(
                                "IntelliPay is loading… please wait.",
                                {
                                  position: "top-right",
                                },
                              );
                              return;
                            }
                          }}
                        >
                          {isIntellipayReady ? "Pay" : "Loading…"}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {!HighSchoolCoach &&
          showPayWidget &&
          selectedPrice > 0 &&
          !isRegisteringFree && (
            <PayWidgetEvents
              userEmail={staticUser.email}
              userId={staticUser.id}
              paymentAmount={staticUser.price}
              eventId={staticUser.eventId}
              subscriptionType={finalSubscriptionType}
              onIntellipayReady={setIsIntellipayReady}
            />
          )}
      </div>
    </>
  );
}

export default PaymentOption;
